/**
 * HOOK: useBpiManualValidation
 * Validation MANUELLE du BPI (bypass paiement) — ticket T1, réunion 25/08.
 *
 * Certains dossiers n'ont aucun paiement BPI dans le système alors que le BPI
 * est bel et bien réglé : équipages venus de l'ancienne plateforme, invités
 * exonérés de pré-inscription, reports d'édition (acompte conservé). Le staff
 * atteste le règlement et saisit le montant à défalquer du solde BED (0 €
 * autorisé = rien n'est déduit, la totalité reste due au BED).
 *
 * Délègue à la RPC `validate_bpi_manually` (migration 00226), réservée
 * admin/superadmin côté serveur. La lecture passe par la table
 * `bpi_manual_validations` (RLS : rôles staff uniquement).
 *
 * Modèle : useBedActions (mapRpcError(message) -> message FR).
 */

import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from './useSupabase';

// Une attestation « BPI réglé » posée à la main. Une seule par (produit, porteur).
export interface BpiManualValidation {
  id: string;
  product_id: string;
  crew_id: string | null;
  contact_id: string | null;
  // Montant défalqué du solde BED, en centimes. 0 = exonéré (rien n'est déduit).
  amount_cents: number;
  note: string | null;
  validated_by: string | null;
  validated_at: string;
}

interface ValidateBpiManuallyParams {
  // Un seul des deux porteurs (le serveur privilégie l'équipage si les deux
  // sont fournis, comme create_bed_payment_case).
  crewId?: string;
  contactId?: string;
  amountCents: number;
  note?: string;
  // Requis pour un porteur contact dont les dossiers couvrent plusieurs
  // produits. Inutile pour un équipage : le serveur lit crews.product_id.
  productId?: string;
}

export interface ValidateBpiManuallyResult {
  success: boolean;
  validation?: BpiManualValidation;
  // false = la validation existait déjà et a été mise à jour (idempotence).
  created?: boolean;
  error?: string;
}

// Mapping code erreur PG (RAISE EXCEPTION dans la RPC 00226) -> message UI FR.
function mapRpcError(message: string): ValidateBpiManuallyResult {
  if (message.includes('bpi_already_paid')) {
    return {
      success: false,
      error:
        "Un paiement BPI réellement encaissé couvre déjà cette inscription (ce dossier ou un membre de l'équipage) : elle ne peut pas être validée manuellement.",
    };
  }
  if (message.includes('bed_already_collected')) {
    return {
      success: false,
      error:
        'Le solde BED de ce dossier a déjà été encaissé sur la base de cette déduction : la validation ne peut plus être retirée. Passez par la comptabilité.',
    };
  }
  if (message.includes('bpi_manual_validation_not_found')) {
    return {
      success: false,
      error: 'Aucune validation manuelle à retirer pour ce dossier.',
    };
  }
  if (message.includes('access_denied') || message.includes('authentication_required')) {
    return {
      success: false,
      error: 'Seuls les comptes Désertours (admin) peuvent valider un BPI manuellement.',
    };
  }
  if (message.includes('bpi_manual_amount_invalid')) {
    return {
      success: false,
      error: 'Le montant validé doit être positif ou nul.',
    };
  }
  if (message.includes('bpi_product_required')) {
    return {
      success: false,
      error:
        "Impossible de déterminer l'édition concernée pour ce contact : validez le BPI depuis la fiche équipage.",
    };
  }
  if (message.includes('bpi_product_mismatch')) {
    return {
      success: false,
      error: "L'édition transmise ne correspond pas à celle de l'équipage.",
    };
  }
  // (00242) Plusieurs membres de l'équipage portent une attestation de montants
  // DIFFÉRENTS : le serveur refuse de choisir (sommer fabriquerait un acompte
  // jamais encaissé). Le staff tranche en validant directement l'équipage.
  if (message.includes('bpi_manual_conflicting_amounts')) {
    return {
      success: false,
      error:
        'Les validations manuelles des membres portent des montants différents : validez directement l’équipage pour trancher.',
    };
  }
  if (message.includes('bpi_owner_required')) {
    return { success: false, error: 'Aucun équipage ni contact fourni.' };
  }
  if (message.includes('crew_not_found')) {
    return { success: false, error: 'Équipage introuvable.' };
  }
  return { success: false, error: 'Erreur lors de la validation manuelle du BPI.' };
}

// Forme du JSONB retourné par validate_bpi_manually (migration 00226).
interface ValidateBpiManuallyRow {
  id: string;
  product_id: string;
  crew_id: string | null;
  contact_id: string | null;
  amount_cents: number;
  note: string | null;
  validated_by: string | null;
  validated_at: string;
  created: boolean;
}

export function useBpiManualValidationActions() {
  const supabase = useSupabase();

  const validateBpiManually = useCallback(
    async ({
      crewId,
      contactId,
      amountCents,
      note,
      productId,
    }: ValidateBpiManuallyParams): Promise<ValidateBpiManuallyResult> => {
      try {
        const { data, error } = await supabase.rpc('validate_bpi_manually', {
          p_crew_id: crewId ?? null,
          p_contact_id: contactId ?? null,
          p_amount_cents: amountCents,
          p_note: note?.trim() ? note.trim() : null,
          p_product_id: productId ?? null,
        });

        if (error) {
          console.error('[useBpiManualValidation] RPC error:', error);
          return mapRpcError(error.message ?? '');
        }

        const row = data as ValidateBpiManuallyRow | null;
        if (!row?.id) {
          console.error('[useBpiManualValidation] unexpected payload:', data);
          return { success: false, error: 'Erreur lors de la validation manuelle du BPI.' };
        }

        return {
          success: true,
          created: row.created,
          validation: {
            id: row.id,
            product_id: row.product_id,
            crew_id: row.crew_id,
            contact_id: row.contact_id,
            amount_cents: row.amount_cents,
            note: row.note,
            validated_by: row.validated_by,
            validated_at: row.validated_at,
          },
        };
      } catch (err) {
        console.error('[useBpiManualValidation] error:', err);
        return { success: false, error: 'Erreur lors de la validation manuelle du BPI.' };
      }
    },
    [supabase]
  );

  // Retrait d'une attestation posée par erreur (RPC revoke_bpi_manual_validation,
  // 00226) : referme le verrou d'émission du lien BED. Refusée par le serveur si
  // le solde BED a déjà été encaissé sur la base de cette déduction.
  const revokeBpiManualValidation = useCallback(
    async ({
      crewId,
      contactId,
      productId,
    }: {
      crewId?: string;
      contactId?: string;
      productId?: string;
    }): Promise<ValidateBpiManuallyResult> => {
      try {
        const { data, error } = await supabase.rpc('revoke_bpi_manual_validation', {
          p_crew_id: crewId ?? null,
          p_contact_id: contactId ?? null,
          p_product_id: productId ?? null,
        });

        if (error) {
          console.error('[useBpiManualValidation] revoke RPC error:', error);
          return mapRpcError(error.message ?? '');
        }

        const row = data as { id?: string } | null;
        if (!row?.id) {
          console.error('[useBpiManualValidation] revoke unexpected payload:', data);
          return { success: false, error: 'Erreur lors du retrait de la validation manuelle.' };
        }
        return { success: true };
      } catch (err) {
        console.error('[useBpiManualValidation] revoke error:', err);
        return { success: false, error: 'Erreur lors du retrait de la validation manuelle.' };
      }
    },
    [supabase]
  );

  return { validateBpiManually, revokeBpiManualValidation };
}

interface UseBpiManualValidationsParams {
  crewId?: string;
  contactId?: string;
  /**
   * (00242) Contacts MEMBRES de l'équipage. Une attestation posée sur le pilote
   * — typiquement avant que l'équipage n'existe — débloque le BED de son
   * équipage côté serveur (`resolve_bpi_manual_proof`, pont `crew_members`).
   * Sans cette liste, la fiche équipage n'affichait pas la preuve qui la
   * débloque, et le staff en reposait une seconde sur l'équipage.
   * Ignoré quand le porteur est un contact.
   */
  memberContactIds?: string[];
  /**
   * (00242) Édition concernée. Une attestation porte TOUJOURS sur un produit
   * précis (report 2026 → 2027) et le serveur filtre dessus partout
   * (`resolve_bpi_manual_proof`). Sans ce filtre, la fiche remontait les
   * attestations de TOUTES les éditions du porteur — et, via le pont, de
   * celles de ses membres : mauvais bandeau, mauvais montant affiché, et un
   * bouton « Retirer » qui supprimait la ligne d'une autre édition.
   * Facultatif : non fourni (porteur contact multi-produits), on ne filtre pas
   * — comportement historique.
   */
  productId?: string;
}

interface UseBpiManualValidationsReturn {
  validations: BpiManualValidation[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Attestations manuelles du porteur (équipage OU contact). Lecture directe de
 * la table : la RLS 00226 la réserve aux rôles staff, un rôle hors liste
 * (`external`) reçoit simplement une liste vide.
 */
export function useBpiManualValidations({
  crewId,
  contactId,
  memberContactIds,
  productId,
}: UseBpiManualValidationsParams): UseBpiManualValidationsReturn {
  const supabase = useSupabase();
  const [validations, setValidations] = useState<BpiManualValidation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sérialisé pour la dépendance du useCallback : un tableau littéral change
  // d'identité à chaque rendu du parent et relancerait la requête en boucle.
  const memberIdsKey = (memberContactIds ?? []).join(',');

  const fetchValidations = useCallback(async () => {
    if (!crewId && !contactId) {
      setValidations([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('bpi_manual_validations')
        .select(
          'id, product_id, crew_id, contact_id, amount_cents, note, validated_by, validated_at'
        )
        .order('validated_at', { ascending: false });

      // (00242) L'ÉDITION d'abord : une attestation vaut pour un produit précis.
      if (productId) {
        query = query.eq('product_id', productId);
      }

      if (crewId) {
        // (00242) Requête élargie au PONT crew_members : l'attestation d'un
        // membre débloque l'équipage côté serveur, la fiche doit la montrer.
        // Les ids sont des UUID (colonne uuid, jamais de saisie libre) : pas de
        // problème d'échappement PostgREST sur le `.or()`.
        const memberIds = memberIdsKey ? memberIdsKey.split(',') : [];
        query = memberIds.length
          ? query.or(`crew_id.eq.${crewId},contact_id.in.(${memberIds.join(',')})`)
          : query.eq('crew_id', crewId);
      } else {
        query = query.eq('contact_id', contactId as string);
      }

      const { data, error: queryError } = await query;
      if (queryError) throw queryError;

      setValidations((data ?? []) as BpiManualValidation[]);
    } catch (err) {
      console.error('[useBpiManualValidations] fetch error:', err);
      setError('Erreur lors du chargement des validations manuelles.');
      setValidations([]);
    } finally {
      setLoading(false);
    }
  }, [supabase, crewId, contactId, memberIdsKey, productId]);

  useEffect(() => {
    void fetchValidations();
  }, [fetchValidations]);

  return { validations, loading, error, refresh: fetchValidations };
}
